import { initializeApp, type FirebaseApp, getApps, getApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, type Functions } from 'firebase/functions';

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
}

export interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  storage: FirebaseStorage;
  functions: Functions;
  config: FirebaseConfig;
}

const REQUIRED_KEYS: ReadonlyArray<keyof FirebaseConfig> = [
  'apiKey',
  'authDomain',
  'projectId',
  'storageBucket',
  'messagingSenderId',
  'appId',
];

export function readFirebaseConfigFromEnv(
  env: Record<string, string | undefined>,
  prefix = 'VITE_FIREBASE_',
): FirebaseConfig {
  const map: Record<keyof FirebaseConfig, string> = {
    apiKey: `${prefix}API_KEY`,
    authDomain: `${prefix}AUTH_DOMAIN`,
    projectId: `${prefix}PROJECT_ID`,
    storageBucket: `${prefix}STORAGE_BUCKET`,
    messagingSenderId: `${prefix}MESSAGING_SENDER_ID`,
    appId: `${prefix}APP_ID`,
  };

  const missing: string[] = [];
  const config = {} as FirebaseConfig;
  for (const key of REQUIRED_KEYS) {
    const envKey = map[key];
    const value = env[envKey];
    if (typeof value !== 'string' || value.trim().length === 0) {
      missing.push(envKey);
      continue;
    }
    config[key] = value.trim();
  }

  if (missing.length > 0) {
    throw new Error(
      `[Firebase config] Missing required environment variables: ${missing.join(
        ', ',
      )}. Please set them in your .env file and restart the dev server.`,
    );
  }
  return config;
}

let cached: FirebaseServices | null = null;

export function initFirebase(config: FirebaseConfig): FirebaseServices {
  if (cached) return cached;
  const app = getApps().length === 0 ? initializeApp(config) : getApp();
  const services: FirebaseServices = {
    app,
    auth: getAuth(app),
    db: getFirestore(app),
    storage: getStorage(app),
    functions: getFunctions(app),
    config,
  };
  cached = services;
  return services;
}

/** Reset the cached services. Intended for tests only. */
export function __resetFirebaseForTests(): void {
  cached = null;
}
